import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Divider, Modal, Select, Spin, Table, Typography } from 'antd';
import {
  listAllDevicesByPage,
  useAddCameraToGroup,
  useListAllDevicesByPage,
} from '../../../../hooks/socket/cameras';
import { debounce } from 'lodash';
import { LoadingOutlined } from '@ant-design/icons';

import './styles.scss';

const mergeData = (prev, current) => {
  return {
    ...prev,
    ...current,
    devices: [...(prev.devices || []), ...(current.devices || [])],
  };
};

export const AddCameraToGroup = ({
  groupId,
  groupName,
  onSuccess,
  namespace,
  onClose,
  getGroupCameras,
}) => {
  const [pages, setPages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDebounce, setIsDebounce] = useState(false);
  const timeRequestRef = useRef(Date.now());
  const searchRef = useRef('');
  const [groupCameras, setGroupCameras] = useState();

  const isGroupCameraLoading = groupCameras === undefined;

  useEffect(() => {
    getGroupCameras(groupId).then(setGroupCameras);
  }, [getGroupCameras, groupId]);

  const data = useMemo(() => {
    return pages.reduce(mergeData, {});
  }, [pages]);

  const fetchCameras = useCallback(
    async ({ page, name }) => {
      setIsLoading(true);
      const response = await listAllDevicesByPage({
        page,
        size: 10,
        namespace,
        deviceType: 'camera',
        timeRequest: timeRequestRef.current,
        name,
      });
      setPages((prev) => [...prev, response]);
      setIsLoading(false);

      return response;
    },
    [namespace]
  );

  const [cameras, setCameras] = useState([]);
  const { send: doAddCamera, isLoading: isLoadingAddCamera } =
    useAddCameraToGroup();

  const handleSearch = useMemo(
    () =>
      debounce(
        (value) => {
          timeRequestRef.current = Date.now();
          setIsDebounce(false);
          searchRef.current = value;

          fetchCameras({
            page: 0,
            size: 10,
            name: value || undefined,
            namespace,
            deviceType: 'camera',
            timeRequest: timeRequestRef.current,
          }).then((res) => {
            setPages([res]);
          });
        },
        500,
        { leading: false, trailing: true }
      ),
    [fetchCameras, namespace]
  );

  const fetchNextPage = useCallback(() => {
    fetchCameras({
      page: pages.length,
      name: searchRef.current,
    });
  }, [pages.length, fetchCameras]);

  const handleScroll = useMemo(
    () => debounce(fetchNextPage, 200, { leading: true, trailing: true }),
    [fetchNextPage]
  );

  const onScroll = (e) => {
    if (isLoading) return;

    if (data.devices.length >= data.total) {
      console.log('end');
      return;
    }

    const isBottom =
      e.target.scrollTop + e.target.clientHeight + 10 >= e.target.scrollHeight;

    if (isBottom) {
      handleScroll(e);
    }
  };

  const onSearch = (value) => {
    setIsDebounce(true);
    handleSearch(value);
  };

  const onOk = () => {
    const newCameras = cameras;

    if (!newCameras.length) {
      return;
    }

    console.log('PAYLOAD', {
      namespace,
      deviceGroupId: groupId,
      serial: newCameras[0].serial,
      indexOf: 1,
    });

    Promise.all(
      newCameras.map((cam) =>
        doAddCamera({
          namespace,
          deviceGroupId: groupId,
          serial: cam.serial,
          indexOf: 1,
        })
      )
    ).then(() => {
      onSuccess(groupId, newCameras);
    });
  };

  const onSelect = (value) => {
    setCameras((prev) => {
      const filtered = prev.filter((cam) => cam.serial !== value);

      return [
        ...filtered,
        data?.devices?.find((device) => device.serial === value),
      ].filter(Boolean);
    });
  };

  const onDeselect = (value) => {
    setCameras((prev) => prev.filter((cam) => cam.serial !== value));
  };

  const [key, setKey] = useState(0);

  const options = useMemo(() => {
    const defaultOptions =
      data?.devices?.map((device) => ({
        label: device.name,
        value: device.serial,
        ...device,
      })) || [];

    const allOptions = [
      ...cameras.map((cam) => ({
        label: cam.name,
        value: cam.serial,
        ...cam,
      })),
      ...defaultOptions,
    ];

    const map = new Map(allOptions.map((item) => [item.value, { 
      ...item,
    }]));

    if (groupCameras) {
      groupCameras.forEach((cam) => {
        if (map.has(cam.serial)) {
          map.set(cam.serial, {
            ...map.get(cam.serial),
            disabled: true,
            className: 'disabled',
          });
        }
      });
    }

    return Array.from(map.values());
  }, [data, cameras, groupCameras]);

  return (
    <Modal
      title={`Add camera to ${groupName}`}
      footer={
        <div>
          <Button
            loading={isLoadingAddCamera}
            type="primary"
            onClick={onOk}
            disabled={!cameras.length}
          >
            Add Camera
          </Button>
        </div>
      }
      visible
      onCancel={onClose}
    >
      <Select
        key={key}
        mode="multiple"
        loading={isLoading || isDebounce}
        onSearch={onSearch}
        onPopupScroll={onScroll}
        value={cameras.map((cam) => cam.serial)}
        options={options}
        onSelect={onSelect}
        onDeselect={onDeselect}
        showSearch
        style={{
          width: 300,
        }}
        placeholder="Select camera to add"
        filterOption={false}
        getPopupContainer={(trigger) => trigger.parentElement}
        allowClear
        dropdownRender={(menu) => {
          return (
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              spinning={isLoading}
            >
              {menu}
            </Spin>
          );
        }}
        onFocus={() => {
          // retain data if no search was done last time
          if (searchRef.current || pages.length === 0) {
            timeRequestRef.current = Date.now();

            fetchCameras({
              page: 0,
              name: '',
            }).then((res) => {
              setPages([res]);
            });
          }
        }}
        onBlur={() => {
          setKey((prev) => (prev + 1) % 5);
        }}
        height={300}
        listItemHeight={32}
        dropdownClassName='add-camera-to-group-dropdown'
      />
      <Divider style={{ margin: '12px 0' }} />
      <Table
        tableLayout="fixed"
        title={() => <h3>Cameras in group</h3>}
        size="small"
        scroll={{ y: 300 }}
        dataSource={groupCameras}
        columns={[
          {
            title: 'Serial',
            dataIndex: 'serial',
            width: 120,
          },
          {
            title: 'Name',
            dataIndex: 'name',
          },
        ]}
        loading={isGroupCameraLoading}
        pagination={{
          showSizeChanger: false,
          size: 'small',
        }}
      />
    </Modal>
  );
};
